import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

// Renderizado explícito: el widget se crea cuando React monta el contenedor, no
// cuando el script termina de bajar. Con el modo implícito (buscar .cf-turnstile
// al cargar la página) el formulario de una SPA todavía no existe en el DOM.
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// El script se baja una sola vez por página aunque haya varios formularios.
let script_promise = null;

function loadTurnstile() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('sin window'));
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (script_promise) {
    return script_promise;
  }

  script_promise = new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    const is_new = !script;

    if (is_new) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
    }

    script.addEventListener('load', () => resolve(window.turnstile));
    script.addEventListener('error', () => {
      // Un bloqueador de anuncios o una red corporativa pueden cortar el
      // script. Se limpia la promesa para que un montaje posterior lo vuelva a
      // intentar en vez de quedar con un rechazo cacheado para siempre.
      script_promise = null;
      reject(new Error('turnstile-script-blocked'));
    });

    if (is_new) {
      document.head.appendChild(script);
    }
  });

  return script_promise;
}

/**
 * Widget de Cloudflare Turnstile.
 *
 * Se implementa a mano sobre la API oficial en vez de traer una librería:
 * el proyecto está en React 16 y los wrappers publicados piden React 18.
 *
 * Expone `reset()` por ref, porque el token es de un solo uso: después de cada
 * envío (haya salido bien o mal) hay que pedirle uno nuevo al widget.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(props, ref) {
  const { siteKey, onVerify, onExpire, onError, theme, language, className } =
    props;

  const container_ref = useRef(null);
  const widget_id_ref = useRef(null);
  const handlers_ref = useRef({});

  // Los callbacks viven en una ref para que un re-render del formulario (cada
  // tecla que escribe el visitante) no destruya y vuelva a crear el widget.
  useEffect(() => {
    handlers_ref.current = { onVerify, onExpire, onError };
  });

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        if (window.turnstile && widget_id_ref.current !== null) {
          window.turnstile.reset(widget_id_ref.current);
        }
      },
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      // Build sin VITE_TURNSTILE_SITE_KEY: se avisa para que el formulario
      // muestre el motivo en vez de dejar el botón muerto sin explicación.
      if (handlers_ref.current.onError) {
        handlers_ref.current.onError('missing-sitekey');
      }
      return undefined;
    }

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container_ref.current) return;
        if (widget_id_ref.current !== null) return;

        widget_id_ref.current = turnstile.render(container_ref.current, {
          sitekey: siteKey,
          theme: theme || 'auto',
          language: language || 'auto',
          callback: (token) => {
            if (handlers_ref.current.onVerify) {
              handlers_ref.current.onVerify(token);
            }
          },
          'expired-callback': () => {
            if (handlers_ref.current.onExpire) handlers_ref.current.onExpire();
          },
          'timeout-callback': () => {
            if (handlers_ref.current.onExpire) handlers_ref.current.onExpire();
          },
          'error-callback': (code) => {
            if (handlers_ref.current.onError) {
              handlers_ref.current.onError(code);
            }
          },
        });
      })
      .catch((e) => {
        if (cancelled) return;
        if (handlers_ref.current.onError) {
          handlers_ref.current.onError(e.message);
        }
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widget_id_ref.current !== null) {
        window.turnstile.remove(widget_id_ref.current);
        widget_id_ref.current = null;
      }
    };
  }, [siteKey, theme, language]);

  return <div ref={container_ref} className={className} />;
});

export default TurnstileWidget;
